/**
 * Configuracion por ambiente del backoffice de recaudaciones (SRE).
 *
 * El .env (solo local, no se versiona; ver .env.example) define REACT_APP_ENV
 * (DEV | QAS | PRD | local), REACT_APP_TOKEN_CLIENT_SECRET y
 * REACT_APP_MDS_CLIENT_SECRET; el resto de valores vive aqui. En el pipeline
 * llegan por las deployment variables.
 *
 * Importante: CRA congela process.env.REACT_APP_ENV dentro del bundle en tiempo
 * de build, asi que un mismo artefacto NO sirve para dos ambientes.
 */

const REGION = "us-east-1";

// Marca de las URLs que vienen del modulo de seguridad (no del API del backoffice).
const SEGURIDAD = "SEGURIDAD";

// Placeholder de un valor aun sin configurar: CAMBIAR_<CLAVE EN MAYUSCULAS>.
const pendiente = (clave) => `CAMBIAR_${clave.toUpperCase()}`;

// ---------------------------------------------------------------------------
// Ruta de cada servicio dentro del API Gateway del backoffice (<ApiBaseUrl><ruta>).
// null = sin backend desplegado (queda en CAMBIAR_); SEGURIDAD = URL propia del
// modulo de seguridad, distinta por ambiente. El orden de las claves se conserva
// en la config final (y en el aviso de pendientes).
// ---------------------------------------------------------------------------
const RUTAS_API = {
  // Sin backend desplegado en ningun ambiente: no existe ninguna ruta account/*
  // en el API Gateway ni en las HTTP API del modulo de seguridad.
  account: null,
  branch: "/",
  channel: "/",
  channelEntityPayment: "",
  channelTransaction: "/",
  client: "/client/",
  conciliationMonitor: "/",
  connectionSftp: "/connectionsftp/",
  // OJO: el front pide "country/list" y esta API expone "country/find_all".
  country: "/Prod/",
  documentType: "/",
  entity: "/entity/",
  limitAmountEntity: "/limit/",
  login: SEGURIDAD,
  message: "",
  monitor: "/",
  paymentHistory: "/payment_history/",
  permissionsAllow: SEGURIDAD,
  rolceConcept: "/",
  serviceSap: "/",
  society: SEGURIDAD,
  summary: "/summary/",
  template: "/template/",
  templateAssign: "/entityTemplate/",
  type: "/type/",
};

// Arma las URLs de los servicios. Sin gateway (base vacia) todas las rutas del
// backoffice quedan en CAMBIAR_; las de seguridad siempre salen de `seguridad`.
const construirApi = (base, seguridad) =>
  Object.entries(RUTAS_API).reduce((api, [clave, ruta]) => {
    if (ruta === SEGURIDAD) {
      api[clave] = seguridad[clave];
    } else if (ruta === null || !base) {
      api[clave] = pendiente(clave);
    } else {
      api[clave] = base + ruta;
    }
    return api;
  }, {});

// Cognito / Hosted UI (el redirect de sign-in y sign-out es el mismo).
const construirCognito = ({
  adProvider,
  clientId,
  domain,
  redirect,
  userPool,
}) => ({
  adProvider,
  clientId,
  domain,
  redirectSignIn: redirect,
  redirectSignOut: redirect,
  region: REGION,
  userPool,
});

// Credenciales machine-to-machine (modulo de seguridad).
const construirToken = (prefijoDominio, clientId) => ({
  accessTokenUrl: `https://${prefijoDominio}-mds.auth.${REGION}.amazoncognito.com/oauth2/token`,
  clientId,
  clientSecret:
    process.env.REACT_APP_TOKEN_CLIENT_SECRET || "CAMBIAR_CLIENTSECRET",
});

// ---------------------------------------------------------------------------
// MDS consolidado (modulo de seguridad). Atiende login/logout/register_user y
// permissions/allow con su propio token (client_credentials + scope). El MDS de
// QAS responde tambien a los DEV de los sistemas integrados: DEV y local lo
// llaman con ?env=dev; QAS no envia ?env (register_user nunca lo lleva).
// society/find_by_user tambien va al MDS: usa el mismo userId que devuelve su
// login (el modulo anterior no conoce esos ids y responde 500).
// ---------------------------------------------------------------------------
const MDS_QAS = "https://1r17ye1p9c.execute-api.us-east-1.amazonaws.com/qas";

const construirMds = (ambiente) => ({
  accessTokenUrl: `https://qas-mds-cc.auth.${REGION}.amazoncognito.com/oauth2/token`,
  clientId: "71rseb1cl4a1ep7m5rpegkah4q",
  clientSecret:
    process.env.REACT_APP_MDS_CLIENT_SECRET || "CAMBIAR_MDSCLIENTSECRET",
  scope: "security/create",
  query: ambiente ? `?env=${ambiente}` : "",
});

const SEGURIDAD_MDS_QAS = {
  login: `${MDS_QAS}/ingress`,
  permissionsAllow: `${MDS_QAS}/permissions/allow`,
  society: `${MDS_QAS}/society/`,
};

// Datos del sistema (iguales en todos los ambientes).
const construirApp = () => ({
  sessionTimeUser: "300",
  systemCode: "1000",
});

// ---------------------------------------------------------------------------
// Codigos de transaccion (admin.ad_transaction, sistema 1000 "SRE")
// ---------------------------------------------------------------------------
// DEV y local: valores en uso hoy (tomados del .env que se versionaba).
const TRANSACCIONES_DEV = {
  account: "30034",
  assignTemplate: "30035",
  branch: "30036",
  channel: "30050",
  channelTransaction: "30049",
  client: "30036",
  conciliationMonitor: "30037",
  connectionSftp: "30039",
  documentType: "30037",
  entity: "30040",
  historyPayment: "30051",
  limitAmount: "30038",
  message: "30042",
  monitor: "30045",
  paymentType: "30039",
  sendPayment: "30044",
  summary: "30038",
  template: "30047",
  type: "30035",
};

// QAS y PRD: mismos 19 codigos (comparados uno a uno el 11-sep-2026).
const TRANSACCIONES_QAS_PRD = {
  account: "30034", // /role_concept          ASIG. DE CONCEPTOS A ROL
  assignTemplate: "30035", // /ASSIGN_TEMPLATE       ASIG. DE PLANTILLAS
  branch: "30048", // /branch                SUCURSAL
  channel: "30050", // /channel               CANAL
  channelTransaction: "30049", // /channel_transaction   TRANSACCIONES POR CANAL
  client: "30036", // /clientsap             CLIENTES
  conciliationMonitor: "30037", // /conciliation_monitor  CONCILIACION BANCARIA
  connectionSftp: "30039", // /connectionsftp        CONEXIONES SFTP (REACT_APP_CONNECTION_SFTP en el .env legacy)
  documentType: "30037",
  entity: "30041", // /collect_entity        ENTIDADES RECAUDADORAS
  historyPayment: "30051", // /history               PARTIDAS
  limitAmount: "30044", // /limitAmountEntity     LIMITE DE MONTO
  message: "30045", // /message               MAESTRO DE MENSAJES
  monitor: "30046", // /monitor               MONITOR DE OPERACION
  paymentType: "30043", // /channelEntityPayment  FORMA DE PAGO
  sendPayment: "30042", // /send_payment          ENVIO DE PAGOS A DEMANDA
  summary: "30038", // /conciliation          CONCILIACION DE PARTIDAS
  template: "30047", // /template              PLANTILLAS
  type: "30040", // /type                  CONF. DE TIPOS
};

// ---------------------------------------------------------------------------
// DEV (y base de local)
// ---------------------------------------------------------------------------
const API_DEV = construirApi(
  "https://91znfyeaxl.execute-api.us-east-1.amazonaws.com/dev",
  SEGURIDAD_MDS_QAS,
);

const COGNITO_DEV = {
  adProvider: "SreAD",
  clientId: "7gnu8g07rgoimvelekkvotgm0t",
  domain: "dev-recaudaciones.auth.us-east-1.amazoncognito.com",
  userPool: "us-east-1_ajsuJVsVK",
};

const environments = {
  // ---------------------------------------------------------------------------
  // DEV — valores en uso hoy (tomados del .env que se versionaba)
  // ---------------------------------------------------------------------------
  DEV: {
    api: { ...API_DEV },
    cognito: construirCognito({
      ...COGNITO_DEV,
      redirect: "https://development.d3ujxp7fxp9v0z.amplifyapp.com",
    }),
    token: construirToken("dev", "2nbi9cn8jposq9689rtgavelmh"),
    mds: construirMds("dev"),
    app: construirApp(),
    transaction: { ...TRANSACCIONES_DEV },
  },

  // ---------------------------------------------------------------------------
  // QAS — APIs, Cognito, m2m y transacciones verificados contra la cuenta 457978769236.
  // Gateway backoffice: x2hwmbzjta (aw-apigw-ue1-qas-cc-recaudaciones, stage qas).
  // documentType y rolceConcept: sin backend desplegado en QAS (tampoco en DEV).
  // ---------------------------------------------------------------------------
  QAS: {
    api: {
      ...construirApi(
        "https://x2hwmbzjta.execute-api.us-east-1.amazonaws.com/qas",
        SEGURIDAD_MDS_QAS,
      ),
      // La URL base es correcta (CountrySecurityLambdaQASeguridad, stage $default)
      // pero el modulo no funcionara hasta alinear la ruta. En DEV pasa lo mismo.
      country: "https://k4nou8zr3b.execute-api.us-east-1.amazonaws.com/",
    },
    cognito: construirCognito({
      adProvider: "SreAAD",
      clientId: "5ljbpec5jhdcsppr8qdnqh4fek",
      domain: "qa-grupogloria.auth.us-east-1.amazoncognito.com",
      redirect: "https://development.d14zpgdbtv9ts5.amplifyapp.com",
      userPool: "us-east-1_PUJDdgp0e",
    }),
    token: construirToken("qas", "34urb9g3thp7uer3boa7bsn7sm"),
    mds: construirMds(""),
    app: construirApp(),
    transaction: { ...TRANSACCIONES_QAS_PRD },
  },

  // ---------------------------------------------------------------------------
  // PRD — cuenta 378338082804.
  // Las APIs del backoffice de recaudaciones aun no estan desplegadas en PRD: quedan
  // en CAMBIAR_ hasta tener el API consolidado (<ApiBaseUrl>/<ruta>, como en QAS).
  // Solo login, permissionsAllow y society (modulo de seguridad) estan verificadas
  // en AWS PRD (16-sep-2026).
  // ---------------------------------------------------------------------------
  PRD: {
    api: construirApi("", {
      // SRELoginSecurityLambdaPRDSeguridad: POST ingress/login, logout, register_user
      login: "https://f3bxsjkxif.execute-api.us-east-1.amazonaws.com/ingress",
      // SREPermissionsSecurityLambdaPRDSeguridad: POST permissions/allow
      permissionsAllow:
        "https://fw1p2a8ar0.execute-api.us-east-1.amazonaws.com/permissions/allow",
      // SRESocietyConfigSecurityLambdaPRDSeguridad: POST society/find_by_user
      society:
        "https://8e1mulw4he.execute-api.us-east-1.amazonaws.com/society/",
    }),
    cognito: construirCognito({
      adProvider: "SreAAD",
      clientId: "6jhrul5go7p2i74ul8jrde1b7u",
      domain: "prd-sre.auth.us-east-1.amazoncognito.com",
      redirect: "https://sremon.serviciosticentro.com",
      userPool: "us-east-1_m25cdkXjD",
    }),
    token: construirToken("prd", "5lg80e4ld6t3o0rp9uh6enic4l"),
    // PRD sigue con el modulo de seguridad anterior: mismo token m2m que el
    // backoffice, sin scope ni ?env, hasta que exista el MDS de PRD.
    mds: {
      ...construirToken("prd", "5lg80e4ld6t3o0rp9uh6enic4l"),
      scope: "",
      query: "",
    },
    app: construirApp(),
    transaction: { ...TRANSACCIONES_QAS_PRD },
  },

  // ---------------------------------------------------------------------------
  // local — igual que DEV salvo los redirects, que apuntan al dev-server (PORT=3000),
  // y documentType / rolceConcept, que quedan sin configurar
  // ---------------------------------------------------------------------------
  local: {
    api: {
      ...API_DEV,
      documentType: pendiente("documentType"),
      rolceConcept: pendiente("rolceConcept"),
    },
    cognito: construirCognito({
      ...COGNITO_DEV,
      redirect: "http://localhost:3000",
    }),
    token: construirToken("dev", "2nbi9cn8jposq9689rtgavelmh"),
    mds: construirMds("dev"),
    app: construirApp(),
    transaction: { ...TRANSACCIONES_DEV },
  },
};

const nombreEnv = (process.env.REACT_APP_ENV || "").trim();
const clave = Object.keys(environments).find(
  (k) => k.toLowerCase() === nombreEnv.toLowerCase(),
);

if (!clave) {
  throw new Error(
    `REACT_APP_ENV="${nombreEnv}" no es un ambiente valido. ` +
      `Valores permitidos: ${Object.keys(environments).join(" | ")}. ` +
      "Revisa el .env (y recuerda reiniciar npm start: CRA solo lee el .env al arrancar).",
  );
}

const env = environments[clave];

// Aviso temprano si el ambiente activo quedo con placeholders sin reemplazar.
// Una URL con "CAMBIAR_" no rompe el build: se descubre recien como un 404 en runtime.
const pendientes = Object.entries(env).flatMap(([grupo, valores]) =>
  Object.entries(valores)
    .filter(
      ([, valor]) => typeof valor === "string" && valor.includes("CAMBIAR"),
    )
    .map(([nombre]) => `${grupo}.${nombre}`),
);

if (pendientes.length) {
  console.error(
    `[env] El ambiente "${clave}" tiene ${pendientes.length} valores sin configurar:\n  ` +
      pendientes.join("\n  "),
  );
}

export { environments, clave as nombreAmbiente };
export default env;
